import React from "react";
import algoliasearch from "algoliasearch/lite";
import { useHistory } from "react-router-dom";
import _ from "lodash";

import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Avatar from "@material-ui/core/Avatar";

import { SearchArea } from "../Utilities/SearchArea";
import { ScoreView, normaliseMEPScore } from "../Utilities/ScoreView";

const client = algoliasearch("V7NYNJIM7C", "439a81efb8f918ee5bf3b0380b0f9f28");

export interface MEP {
  objectID: string;
  id: number;
  name: string;
  image: string;
  nationality: string;
  group: string;
  party: string;
  score: number;
  word_cloud: string;
  activities: Activity[];
  epref: string;
  twitter?: string;
  mail?: string;
}

export interface Activity {
  title: string;
  amendments: number;
  score: number;
  subjects: Subject[];
  epref: string;
}

export interface Subject {
  title: string;
  score: number;
}

export const MEPView: React.FC = () => {
  const [index] = React.useState(client.initIndex("meps"));

  const [currentQuery, setCurrentQuery] = React.useState<string>("");
  const [currentHits, setCurrentHits] = React.useState<MEP[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(0);

  const submitSearch = async () => {
    const searchResults = await index.search(currentQuery);
    setCurrentHits(searchResults.hits as MEP[]);
    setCurrentPage(searchResults.page + 1);
  };

  React.useEffect(() => {
    submitSearch();
  }, []);

  React.useEffect(() => {
    window.onscroll = _.throttle((event: any) => {
      if (
        window.innerHeight + window.pageYOffset >=
        document.body.offsetHeight
      ) {
        index
          .search(currentQuery, {
            page: currentPage,
          })
          .then((result) => {
            setCurrentHits((prev) => [...prev, ...(result.hits as MEP[])]);
            setCurrentPage(result.page + 1);
          });
      }
    }, 500);
  }, [currentQuery, currentPage, setCurrentPage, setCurrentHits, index]);

  return (
    <>
      <Container maxWidth={false}>
        <SearchArea
          currentQuery={currentQuery}
          setCurrentQuery={setCurrentQuery}
          onClick={submitSearch}
        />
        <ListArea mepList={currentHits} />
      </Container>
    </>
  );
};

const ListArea: React.FC<{ mepList: MEP[] }> = ({ mepList }) => {
  return (
    <section>
      {mepList.map((mep) => (
        <MEPListItem key={mep.id} mep={mep} />
      ))}
    </section>
  );
};

const MEPListItem: React.FC<{ mep: MEP }> = ({ mep }) => {
  const history = useHistory();

  return (
    <Card
      style={{ marginBottom: "20px" }}
      onClick={() => {
        history.push(`/mep/${mep.objectID}`);
      }}
    >
      <CardContent>
        <Grid container direction="row">
          <Grid item style={{ marginRight: "10px" }}>
            <Avatar
              alt={mep.name}
              src={mep.image}
              style={{ height: "75px", width: "75px" }}
            />
          </Grid>
          <Grid item xs={true}>
            <Typography variant="h5" gutterBottom>
              {mep.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Political party
            </Typography>
            <Typography>{mep.party}</Typography>
            <Typography variant="caption" color="textSecondary">
              Group
            </Typography>
            <Typography>{mep.group}</Typography>
            <Typography variant="caption" color="textSecondary">
              Nationality
            </Typography>
            <Typography>{mep.nationality}</Typography>
            <ScoreView
              score={mep.score}
              scoreNormalised={normaliseMEPScore(mep.score)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
